import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import { useQuasar } from "quasar";
import { numberFormat } from "src/assets/scripts/utilities/helpers";
import PConfirmDialog from "src/components/PConfirmDialog.vue";

export function useSendForApproval({
  formData,
  closeForm,
  validateForm,
  disableSubmitButton,
}) {
  const $q = useQuasar();

  const {
    putAdvancePaymentRequest,
    sendPaymentRequestForApproval,
    selfApprovePaymentRequest,
  } = usePaymentRequestStore();

  const loading = ref(false);

  const isAdvanceRequest = computed(() => {
    return unref(formData).is_advance;
  });

  const isReleaseRequest = computed(() => {
    return ["CAP", "CFP", "CGR"].includes(unref(formData).request_against);
  });

  const onConfirm = async () => {
    try {
      loading.value = true;

      if (unref(isAdvanceRequest)) {
        await putAdvancePaymentRequest(unref(formData));
      } else if (unref(isZeroNetPayable)) {
        await selfApprovePaymentRequest(unref(formData));
      } else {
        await sendPaymentRequestForApproval(unref(formData));
      }

      closeForm(true);
    } catch (error) {
      console.error(error);
    } finally {
      loading.value = false;
    }
  };

  const confirmSelfApproval = () => {
    let label = `Do you confirm self approving a payment request of ${numberFormat(
      {
        number: unref(formData).net_payable ?? 0,
        style: "currency",
      },
    )} for ${unref(formData)?.seller_info?.bank_beneficiary_name ?? ""}?`;

    if (unref(formData)?.applied_credits_lineitems?.length) {
      const used_credits = unref(formData).used_credits;

      const lineItemsText = unref(formData)
        ?.applied_credits_lineitems.map((payment) => {
          return `${numberFormat({
            number: payment.applied_amount,
            style: "currency",
          })} from ${payment.number}`;
        })
        .join(", ");

      label = `You have applied a total credit of ${numberFormat({
        number: used_credits,
        style: "currency",
      })} (${lineItemsText}) against Bill ${unref(formData)?.bill_id?.number} of vendor "${unref(formData)?.seller_info?.shop_name}" for this payment request.
Do you want to confirm this action?`;
    }

    $q.dialog({
      component: PConfirmDialog,
      componentProps: {
        cancelLabel: "Cancel",
        labelClass: "text-center q-ma-xl rounded-borders",
        submitLabel: "Confirm",
        cardStyle: { maxWidth: "720px" },
        label: label,
        onConfirm,
      },
    });
  };

  const confirmSendForApproval = async () => {
    const approversName = unref(formData)
      .approvers.map(
        (approver) => useChangeCase(approver?.name ?? "", "capitalCase").value,
      )
      .join(", ");

    if (await validateForm()) {
      let label = `Do you confirm sending a payment request of ${numberFormat({
        number: unref(formData).net_payable ?? 0,
        style: "currency",
      })} for ${
        unref(formData)?.seller_info?.bank_beneficiary_name ??
        unref(formData)?.seller?.bank_beneficiary_name ??
        ""
      } to ${unref(formData)
        .approvers.map(
          (approver) =>
            useChangeCase(approver?.name ?? "", "capitalCase").value,
        )
        .join(", ")} for approval?`;

      if (unref(isReleaseRequest)) {
        label = `Do you confirm sending a payment request to release payment against Purchase Bill ${unref(formData).bill_id?.number} and Invoice  ${unref(formData).invc_id?.number} to ${approversName} for approval ?`;
      }

      $q.dialog({
        component: PConfirmDialog,
        componentProps: {
          cancelLabel: "Cancel",
          labelClass: "text-center q-ma-xl rounded-borders",
          cardStyle: { maxWidth: "720px" },
          submitLabel: "Confirm",
          label,
          onConfirm,
        },
      });
    }
  };

  const isZeroNetPayable = computed(() => {
    return unref(formData).net_payable === 0;
  });

  const buttonLabel = computed(() => {
    if (unref(isAdvanceRequest)) {
      return "Send for Approval";
    }

    return unref(isZeroNetPayable) ? "Self Approve" : "Send for Approval";
  });

  const onClick = computed(() => {
    if (unref(isAdvanceRequest)) {
      return confirmSendForApproval;
    }

    return unref(isZeroNetPayable)
      ? confirmSelfApproval
      : confirmSendForApproval;
  });

  const sendForApprovalButton = computed(() => {
    return {
      loading: unref(loading),
      label: unref(buttonLabel),
      type: "button",
      onClick: unref(onClick),
      disable: unref(disableSubmitButton) || false,
    };
  });

  return {
    sendForApprovalButton,
  };
}
